import { clamp } from '@Utils/Math.js'
import { CanvasTexture, RepeatWrapping, NoColorSpace } from 'three/webgpu'

export function createTextureCanvas(image, properties = {}) {
	if (image === null || image === undefined) {
		//console.error('createTextureCanvas: image is null', properties.tag)
		return null
	}
	const {
		colorSpace = NoColorSpace,
		repeatAmount = null,
		rotation = null,
	} = properties

	let tex = new CanvasTexture(image)
	tex.colorSpace = colorSpace
	if (repeatAmount) {
		tex.wrapS = tex.wrapT = RepeatWrapping
		tex.repeat.set(repeatAmount, repeatAmount)
	}
	if (rotation) {
		tex.rotation = rotation
	}
	return tex
}
export function createCanvas(width, height, tag) {
	if (typeof document === 'undefined') {
		let c = new OffscreenCanvas(width, height)
		c.oncontextlost = function (event) {
			console.error(`${tag}: context lost`)
		}
		c.oncontextrestored = function (event) {
			console.info(`${tag}: context restored`)
		}
		return c
	}
	let c = document.createElement('canvas')
	c.width = width
	c.height = height
	return c
}
export function createContext(width, height, tag) {
	let canvas = createCanvas(width, height, tag)
	let context = getContext(canvas)
	return [canvas, context]
}
export function getContext(canvas, tag) {
	let context = canvas.getContext('2d', {
		desynchronized: true,
		//preserveDrawingBuffer: true,
		willReadFrequently: true,
		// imageSmoothingEnabled: true,
		// imageSmoothingQuality: 'high'
	})
	if (tag) {
		canvas.oncontextlost = function (event) {
			console.error(`${tag}: context lost`)
		}
		canvas.oncontextrestored = function (event) {
			console.info(`${tag}: context restored`)
		}
	}
	canvas.imageSmoothingQuality = 'high'
	return context
}
export function createCanvasFromImageData(data) {
	let canvas = createCanvas(data.width, data.height, 'Canvas.createCanvasFromImageData')
	let context = getContext(canvas)
	context.putImageData(data, 0, 0)
	return canvas
}
export function createPath(coordinates) {
	let path = new Path2D()
	path.moveTo(coordinates[0][0], coordinates[0][1])
	for (let i = 1; i < coordinates.length; i++) {
		path.lineTo(coordinates[i][0], coordinates[i][1])
	}
	return path
}
export function display(options) {
	if (typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope) {
		return
	}
	if (!options) {
		return
	}
	if (options['getContext']) {
		document.body.appendChild(options)
	} else if (options.canvas) {
		document.body.appendChild(options.canvas)
	} else if (options.imageData) {
		let canvas = createCanvas(options.imageData.width, options.imageData.height)
		let context = canvas.getContext('2d')
		context.putImageData(options.imageData, 0, 0)
		document.body.appendChild(canvas)
	}
}
export function getPixel(imageData, x, y, def = [128, 128, 128, 255]) {
	if (!imageData) {
		return def
	}
	x = clamp(Math.floor(x), 0, imageData.width)
	y = clamp(Math.floor(y), 0, imageData.height)
	const offset = y * imageData.width * 4 + x * 4
	return [imageData.data[offset + 0], imageData.data[offset + 1], imageData.data[offset + 2], imageData.data[offset + 3]]
}

export async function addLine(context, coordinates) {
	if (coordinates.length < 1) {
		return
	}
	context.moveTo(coordinates[0][0], coordinates[0][1])
	for (let i = 1; i < coordinates.length; i++) {
		context.lineTo(coordinates[i][0], coordinates[i][1])
	}
}
let conversionCanvas = new OffscreenCanvas(100, 100)
let conversionContext = conversionCanvas.getContext('bitmaprenderer', {
	//desynchronized: true
})
export async function convertToBlob({
	image,
	type = 'image/jpeg',
	quality = 0.7
}, tag) {
	// if (tag) {
	// 	console.log(`Canvas.convertToBlob: ${tag}`)
	// }
	conversionCanvas.width = image.width
	conversionCanvas.height = image.height
	conversionContext.transferFromImageBitmap(image)
	return await conversionCanvas.convertToBlob({ type: type, quality: quality })
}
export async function convertCanvasToBlob(canvas, options = {
	type: 'image/jpeg',
	quality: 0.7
}, tag) {
	//console.log(`Canvas.convertCanvasToBlob: ${tag}`)
	return new Promise((resolve, reject) => {
		canvas.toBlob(function (blob) {
			resolve(blob)
		}, options.type, options.quality)
	})
}

/**
 * Calculate tile grid for tiled rendering
 * @param {number} width - Total image width
 * @param {number} height - Total image height
 * @param {number} tileSize - Size of each tile (width and height)
 * @param {number} overlap - Overlap between tiles in pixels
 * @returns {Array} Array of tile descriptors with viewport and stitching coordinates
 */
export function calculateTileGrid(width, height, tileSize, overlap) {
	const tiles = []
	const cols = Math.ceil(width / tileSize)
	const rows = Math.ceil(height / tileSize)
	
	for (let row = 0; row < rows; row++) {
		for (let col = 0; col < cols; col++) {
			// Calculate viewport bounds with overlap
			const startX = Math.max(0, col * tileSize - overlap)
			const startY = Math.max(0, row * tileSize - overlap)
			const endX = Math.min(width, (col + 1) * tileSize + overlap)
			const endY = Math.min(height, (row + 1) * tileSize + overlap)
			
			// Actual tile dimensions including overlap
			const tileWidth = endX - startX
			const tileHeight = endY - startY
			
			// Stitching coordinates (where to place in final image, excluding overlap)
			const stitchX = col * tileSize
			const stitchY = row * tileSize
			const stitchWidth = Math.min(tileSize, width - stitchX)
			const stitchHeight = Math.min(tileSize, height - stitchY)
			
			// Source coordinates within the tile (skip overlap on left/top edges)
			// For left edge: skip left overlap if not first column
			// For top edge: skip top overlap if not first row
			const sourceX = col > 0 ? overlap : 0
			const sourceY = row > 0 ? overlap : 0
			
			tiles.push({
				row,
				col,
				// Viewport bounds in scene coordinates
				viewport: {
					offsetX: startX - width / 2,  // Convert to scene center origin
					offsetY: startY - height / 2,
					width: tileWidth,
					height: tileHeight
				},
				// Stitching coordinates in final image
				stitch: {
					destX: stitchX,
					destY: stitchY,
					destWidth: stitchWidth,
					destHeight: stitchHeight,
					sourceX,
					sourceY
				}
			})
		}
	}
	
	return tiles
}

/**
 * Stitch tile ImageBitmaps into final composite image
 * @param {Array} tiles - Array of {tile: ImageBitmap, descriptor: tileDescriptor}
 * @param {number} finalWidth - Final image width
 * @param {number} finalHeight - Final image height
 * @returns {Promise<ImageBitmap>} Final stitched ImageBitmap
 */
export async function stitchTiles(tiles, finalWidth, finalHeight) {
	const [canvas, context] = createContext(finalWidth, finalHeight, 'Canvas.stitchTiles')
	
	for (const { tile, descriptor } of tiles) {
		const { destX, destY, destWidth, destHeight, sourceX, sourceY } = descriptor.stitch
		
		// Draw tile onto final canvas
		context.drawImage(
			tile,
			sourceX, sourceY, destWidth, destHeight,  // Source rect
			destX, destY, destWidth, destHeight        // Dest rect
		)
		
		// Clean up tile ImageBitmap
		tile.close()
	}
	
	// Convert final canvas to ImageBitmap
	return await createImageBitmap(canvas)
}